import styled from 'styled-components';
import { UiColors } from 'assets/styles/old/global';

/**
 * Local copy of modules/Modals/styles.js' ModalWrapper plus a width cap for the new design.
 *
 * Deliberately NOT `styled(Shared.ModalWrapper)`: modules/Modals/styles.js sits on the known
 * circular import chain (modules/Modals/styles -> ui-kit -> modules -> modules/Modals/index ->
 * this file), so the shared export is still undefined when this module initialises and wrapping it
 * throws at import time. DetachFromCampaignModal/styles.js:8 is the standing example.
 *
 * The cap is what "reduce the width" needs: ui-kit/Modal hardcodes `autoWidth`, so the frame is
 * shrink-to-fit and the long instruction lines below stretched it to the full viewport. Capping the
 * child caps the frame's max-content size, which gives the list a readable measure and leaves room
 * to centre the upload controls under it.
 */
export const ModalWrapper = styled.div`
  min-height: 200px;
  min-width: 400px;

  html[data-new-ui='true'] & {
    max-width: 760px;

    /* The submit button is the only direct-child button here (Choose File is nested in
       ButtonWrapper), so this centres it without reaching into the shared ModalButtonSet. */
    > button {
      width: fit-content;
      margin: 0 auto;
    }
  }
`;

export const DropZone = styled.div`
  border: 2px dashed #cccccc;
  border-radius: 4px;
  padding: 20px;
  height: 200px;
  width: 200px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  text-wrap: wrap;

  &:hover {
    background-color: #f0f0f0;
  }

  /* New design: a 200x200 box pinned to the left edge read as a stray sidebar. It becomes a centred
     drop target with its prompt centred inside, capped so it stays a target rather than a slab. */
  html[data-new-ui='true'] & {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: 420px;
    height: auto;
    min-height: 120px;
    margin: 0 auto 16px;
    padding: 24px;
    text-align: center;
    border-radius: var(--panel-radius);
    border-color: var(--grey);

    &:hover {
      background-color: ${UiColors.cloudColor};
    }
  }
`;

export const ChosenFileText = styled.p`
  margin: 0;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
`;

export const FileInput = styled.input`
  display: none;
`;

export const ChooseFileButton = styled.button`
  background: var(--rgb-grey);
  padding: 2px 10px;
  min-height: 30px;
  min-width: 100px;

  /* This one really was square in both designs: the class declared no radius, so the global
     "button { border-radius: 0 }" in assets/styles/base.scss won by default rather than by
     specificity. The new design gives it the OUTLINE surface the redesigned secondary controls use. */
  html[data-new-ui='true'] & {
    padding: 0 16px;
    min-height: 34px;
    border: 1px solid ${UiColors.strokeColor};
    border-radius: var(--btn-radius);
    background: ${UiColors.fillWhiteColor};
    color: ${UiColors.forestColor};
    font-weight: var(--btn-weight);

    &:hover {
      background: ${UiColors.cloudColor};
    }

    &:focus-visible {
      outline: 2px solid var(--dodger-blue);
      outline-offset: 2px;
    }
  }
`;

export const ModalDescription = styled.p`
  font-size: 18px;
  margin-bottom: 10px;
`;

export const ButtonWrapper = styled.div`
  display: flex;
  margin: 20px 0;
  align-items: center;

  button {
    margin-right: 20px;
  }
`;

/**
 * The Choose File + filename row. Separate from ButtonWrapper because that one also carries the
 * "Download template" button at the top of the modal, which stays left-aligned — only the upload
 * controls centre under the drop target.
 */
export const FilePickerRow = styled(ButtonWrapper)`
  html[data-new-ui='true'] & {
    justify-content: center;
    gap: 12px;

    button {
      margin-right: 0;
    }
  }
`;

export const List = styled.ul`
  font-size: 18px;
  margin: 0 35px 15px;

  li {
    display: list-item;
    list-style: disc;
  }
`;
